/**
 * CoSingery — **公開分享頁**（`/s/<token>`；`decisions/031`）
 *
 * 為何要（產品負責人 2026-09-23 iPhone 實測）：「android can share through whatsapp, but iphone can't」
 *   ＋「share the whole page for the 'My feeling Diary' … the date, song name, the feeling,
 *   the song with player, and the lyrics」→ 分享**一條連結**（唔靠檔案分享；iPhone 一定開得到）。
 *
 * ⚠️ 呢一頁係**公開**（唔需要 deviceId／唔屬於任何 app 狀態）：
 *   · 只顯示後端**核准嘅欄位**（`/api/share/:token` 嘅回應；見 `_lib/share/routes.ts` 私隱規則）
 *   · 失敗要**照實講**：`not_found`（冇呢條 link）／`revoked`（已撤回）／`expired`（已過期）
 *   · ⚠️ **唔准**喺呢頁顯示用戶身份／deviceId／其他歌
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import BrandMark from '../components/BrandMark';

interface PublicShare {
  token: string;
  createdAt: string;
  expiresAt: string;
  views: number;
  audioUrl: string | null;
  song: {
    date: string;
    title: string;
    journal: string | null;
    lyrics: string | null;
    hasAudio: boolean;
    /** ⭐ 2026-09-23#6：分享只會播**揀咗嗰版**；呢度照實報係第幾版 */
    variant?: { index: number; total: number };
  };
}

/** `2026-09-23T…` → 跟語言嘅日期（讀唔到就照實顯示原文） */
function formatDate(iso: string, locale: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
}

const HAND = { fontFamily: 'var(--font-hand, inherit)', lineHeight: 'var(--diary-line, 28px)' } as const;

export default function SharePage() {
  const { t, i18n } = useTranslation();
  const { token = '' } = useParams();
  const [data, setData] = useState<PublicShare | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    /** ⚠️ 分享頁唔應該被搜尋引擎收錄 */
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(`/api/share/${encodeURIComponent(token)}`, { cache: 'no-store' });
        const body = (await res.json().catch(() => ({}))) as Partial<PublicShare> & { error?: string };
        if (!alive) return;
        if (!res.ok) {
          /** 照實講：邊種失效就講邊種 */
          setError(body.error ?? `http_${res.status}`);
          return;
        }
        setData(body as PublicShare);
      } catch (err) {
        if (alive) setError(String(err).slice(0, 80));
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const failure = (key: string): string =>
    key === 'revoked' ? t('share_page_revoked') : key === 'expired' ? t('share_page_expired') : key === 'not_found' ? t('share_page_not_found') : `${t('share_page_failed')}（${key}）`;

  return (
    <div className="page">
      <div className="page__scroll" style={{ padding: '16px' }}>
        <p className="footnote" style={{ margin: '0 0 10px', textAlign: 'center' }}>
          {t('share_page_from')}
        </p>
        {error ? (
          <p className="page__error" role="alert" data-testid="share-page-error" style={{ textAlign: 'center' }}>
            {failure(error)}
          </p>
        ) : !data ? (
          <p className="page__loading" style={{ textAlign: 'center' }}>
            {t('chat_thinking')}
          </p>
        ) : (
          <article
            data-testid="share-page"
            style={{
              background: 'var(--color-card)',
              border: '1px solid var(--color-divider)',
              borderRadius: '18px',
              padding: '16px',
            }}
          >
            <p className="footnote" data-testid="share-page-date" style={{ margin: 0 }}>
              {formatDate(data.song.date, i18n.language)}
            </p>
            <h1 style={{ margin: '6px 0 12px', fontSize: 'var(--font-size-title, 22px)' }}>{data.song.title}</h1>

            {/* ⭐ 2026-09-23#6：多版本時照實講「分享嘅係第幾版」（只會播揀咗嗰版） */}
            {(data.song.variant?.total ?? 0) > 1 ? (
              <p className="footnote" data-testid="share-page-version" style={{ margin: '0 0 10px' }}>
                {t('share_page_version', {
                  n: (data.song.variant?.index ?? 0) + 1,
                  total: data.song.variant?.total ?? 1,
                })}
              </p>
            ) : null}

            {data.song.journal ? (
              <>
                <h2 className="footnote" style={{ margin: '0 0 4px' }}>
                  {t('diary_write_label')}
                </h2>
                <p data-testid="share-page-journal" style={{ ...HAND, margin: '0 0 14px', whiteSpace: 'pre-wrap' }}>
                  {data.song.journal}
                </p>
              </>
            ) : null}

            {data.song.hasAudio && data.audioUrl ? (
              <div style={{ margin: '0 0 14px' }}>
                {/* ⭐ 公開頁用**原生播放器**（唔靠 app 內受控播放器；任何瀏覽器都播到） */}
                <audio
                  data-testid="share-page-audio"
                  controls
                  preload="metadata"
                  src={data.audioUrl}
                  style={{ width: '100%' }}
                />
              </div>
            ) : null}

            {data.song.lyrics ? (
              <>
                <h2 className="footnote" style={{ margin: '0 0 4px' }}>
                  {t('diary_lyrics_title')}
                </h2>
                <p data-testid="share-page-lyrics" style={{ ...HAND, margin: 0, whiteSpace: 'pre-wrap' }}>
                  {data.song.lyrics}
                </p>
              </>
            ) : null}

            <p className="footnote" data-testid="share-page-meta" style={{ margin: '14px 0 0', textAlign: 'center' }}>
              {t('share_page_meta', { views: data.views })}
            </p>
          </article>
        )}
        {/* ⭐ 2026-09-23#9：收到連結嘅人第一眼就認得個品牌（同 app icon 同一套幾何） */}
        <div
          style={{ marginTop: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
        >
          <BrandMark size={26} />
          <a href="/" style={{ color: 'var(--color-text)' }}>
            {t('app_name')}
          </a>
        </div>
      </div>
    </div>
  );
}
